import React, { useEffect, useState, useContext, useCallback } from 'react';
import AuthContext from '../services/AuthContext';
import axiosInstance from '../services/axiosInstance';
import GicLocationModal from './GicLocationModal';
import { useToast } from '@chakra-ui/react';

/**
 * GicLocationCheck — mounts globally in App.js
 * Checks once (after 3 s) whether the logged-in student is a GIC student
 * who hasn't submitted their location yet. If so, opens the non-closable modal.
 * Once submitted the flag `locationSubmitted` is set to true on the backend,
 * so the popup will never appear again.
 */
export default function GicLocationCheck() {
  const { user } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const checkEligibility = useCallback(async () => {
    if (!user || user.role !== 'student') return;

    try {
      const { data } = await axiosInstance.get('/users/gic-location-eligibility');
      if (data.success && data.isEligible) {
        setIsEligible(true);
        setIsOpen(true);
      }
    } catch (error) {
      console.error('GicLocationCheck: error checking eligibility', error);
    }
  }, [user]);

  useEffect(() => {
    const timer = setTimeout(checkEligibility, 3000);
    return () => clearTimeout(timer);
  }, [checkEligibility]);

  const handleSubmit = async (wilaya, moughataa) => {
    setIsLoading(true);
    try {
      await axiosInstance.post('/users/gic-location', { wilaya, moughataa });
      setIsOpen(false);
      toast({
        title: 'تم الحفظ بنجاح',
        description: `تم تسجيل موقعك: ${wilaya} — ${moughataa}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      console.error('GicLocationCheck: submit error', error);
      toast({
        title: 'خطأ',
        description: 'حدث خطأ أثناء الحفظ، يرجى المحاولة مجدداً.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isEligible) return null;

  return (
    <GicLocationModal
      isOpen={isOpen}
      onSubmit={handleSubmit}
      isLoading={isLoading}
    />
  );
}
